import { ChangeDetectionStrategy, Component, computed, effect, input, model, signal } from '@angular/core';
import { WorkingTimePeriodDto } from 'src/app/models/dtos/absences/working-time-periods-dto';
import { toDateKey } from 'src/app/absences/services/working-time-periods';

@Component({
  selector: 'app-working-time-period-picker',
  templateUrl: './working-time-period-picker.component.html',
  styleUrls: ['./working-time-period-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WorkingTimePeriodPickerComponent {
  periods = input.required<WorkingTimePeriodDto[]>();
  periodSelection = model<WorkingTimePeriodDto | null>(null);
  showFinished = signal(false);
  readonly dateKey = toDateKey;

  private readonly todayKey = toDateKey(new Date());

  filteredPeriods = computed(() =>
    this.showFinished() ? this.periods() : this.periods().filter((period) => !this.isFinished(period)),
  );

  constructor() {
    effect(() => {
      const filtered = this.filteredPeriods();
      const current = this.periodSelection();
      const currentInFilteredList = current ? filtered.find((period) => this.isSamePeriod(period, current)) ?? null : null;
      const next = currentInFilteredList ?? this.defaultPeriod(filtered);

      if (next !== current) {
        this.periodSelection.set(next);
      }
    });
  }

  comparePeriods = (a: WorkingTimePeriodDto | null, b: WorkingTimePeriodDto | null): boolean =>
    !!a && !!b ? this.isSamePeriod(a, b) : a === b;

  private isSamePeriod = (a: WorkingTimePeriodDto, b: WorkingTimePeriodDto): boolean =>
    toDateKey(a.StartDate) === toDateKey(b.StartDate);

  private isFinished = (period: WorkingTimePeriodDto): boolean =>
    period.EndDate != null && toDateKey(period.EndDate) < this.todayKey;

  /** Period containing today, otherwise the nearest future one, otherwise the latest one. */
  private defaultPeriod = (periods: WorkingTimePeriodDto[]): WorkingTimePeriodDto | null =>
    periods.find((period) => toDateKey(period.StartDate) <= this.todayKey && !this.isFinished(period)) ??
    periods.find((period) => toDateKey(period.StartDate) > this.todayKey) ??
    periods[periods.length - 1] ??
    null;
}
